import React, { FormEvent, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styles from './CybersourceFormV2.module.scss';
import { CardVisualizer } from '../CardVisualizer';
import NumberFormat from 'react-number-format';
import AlertBadge from 'components/common/AlertBadge';
import Button from 'components/common/Button';
import { CybElementId } from '@wallets/sdk';
import Terms from 'components/common/Terms';
import { getTermsAndConditionByCountry } from 'consts';
import { country } from '../../../consts';

export type CybersourceFormV2Props = {
    isOpen?: boolean;
    isLoading?: boolean;
    error?: null | string;
    isSending?: boolean;
    isDisabled?: boolean;
    remember?: boolean;
    onClose?: () => void;
    onSubmit?: (event: FormEvent) => void;
    onChangeRemember?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export const CybersourceFormV2: React.FC<CybersourceFormV2Props> = (props) => {
    const [container] = useState(() => document.createElement('div'));
    const [termsChecked, setTermsChecked] = useState<boolean>(false);
    const root = document.body;

    useEffect(() => {
        root.appendChild(container);
        return () => {
            root.removeChild(container);
        };
    }, [container, root]);

    const component = (
        <div className={styles.wrapper}>
            <div className={`${styles.modal}`} data-testid="modal">
                <div className={styles.header}>
                    <div className={styles.title}>Ingresa tu tarjeta de crédito</div>
                    <button onClick={props.onClose} data-testid="close-button" />
                </div>
                <div className={styles.body}>
                    <div className={styles.container}>
                        <div className={styles.content}>
                            {props.remember && (
                                <div className={styles.notificationMessage}>
                                    *Validaremos tu tarjeta con un{' '}
                                    <strong>cobro aleatorio entre $50 y $500</strong> que serán
                                    reversados.*
                                </div>
                            )}
                            <div className={styles.cardVisualizer}>
                                <CardVisualizer
                                    method=""
                                    pan="1111 1111 1111 1111"
                                    cvc="123"
                                    expiry="12/34"
                                    showPanPassword
                                    issuer=""
                                />
                            </div>
                            <form
                                onSubmit={props.onSubmit}
                                id={CybElementId.CYB_MFV2_FORM_ID}
                                data-testid="phub-cybv3-form"
                                className={`${styles.form} ${
                                    props.isLoading ? styles['hidden'] : ''
                                }`}
                            >
                                <div className={styles['row']}>
                                    <div className={styles['field']}>
                                        <span className={styles['number-label']}>
                                            Número de la Tarjeta *
                                        </span>

                                        <div
                                            id={CybElementId.CYB_MFV2_NUMBER_CONTAINER}
                                            className={styles['flex']}
                                            data-testid="cybv3-number-container"
                                        />
                                    </div>
                                </div>

                                <div className={styles['row']} style={{ marginBottom: 15 }}>
                                    <div className={styles['exp-date-label']}>Mes/Año *</div>
                                    <NumberFormat
                                        id={CybElementId.CYB_MFV2_EXPDATE_ID}
                                        type="text"
                                        className={`${styles['field']} ${styles['flex']}`}
                                        format="##/##"
                                        placeholder="MM/AA"
                                        data-testid="cybv3-exp-date"
                                    />

                                    <div className={styles['field']}>
                                        <span className={styles['cvv-label']}>CVV *</span>
                                        <div
                                            id={CybElementId.CYB_MFV2_SECURITY_CODE_CONTAINER}
                                            className={styles['flex']}
                                            data-testid="cybv3-security-container"
                                        />
                                    </div>
                                </div>

                                <div
                                    className={styles['row']}
                                    style={{
                                        display: 'none',
                                    }}
                                >
                                    <div className={styles['remember']}>
                                        <input
                                            id={CybElementId.CYB_MFV2_REMEMBER}
                                            type="checkbox"
                                            name="remember"
                                            data-testid="cybv3-remember"
                                            onChange={props.onChangeRemember}
                                            checked={props.remember}
                                        />
                                        <label htmlFor="phub-cybv3-remember">
                                            Guardar para futuras compras
                                        </label>
                                    </div>
                                </div>

                                <div style={{ marginBottom: 10 }}>
                                    <Terms
                                        id="confirmation-terms-checkbox-cybersource"
                                        onChange={setTermsChecked}
                                        termsLocation={getTermsAndConditionByCountry(country)}
                                        withCheckbox={true}
                                    />
                                </div>

                                {props.error && (
                                    <div className={styles['error']} style={{ marginBottom: 15 }}>
                                        <AlertBadge theme="danger" size="medium">
                                            {props.error}
                                        </AlertBadge>
                                    </div>
                                )}

                                <div className={styles['row']}>
                                    <div
                                        className={styles['button']}
                                        data-testid="cybv3-pay-button-container"
                                    >
                                        <Button
                                            id={CybElementId.CYB_MFV2_PAY_BUTTON_ID}
                                            theme="primary"
                                            size="medium"
                                            loading={props.isSending}
                                            disabled={
                                                props.isSending || props.isDisabled || !termsChecked
                                            }
                                            onClick={props.onSubmit}
                                        >
                                            {props.remember ? 'Inscribir mi tarjeta' : 'Continuar'}
                                        </Button>
                                    </div>
                                </div>
                                <div
                                    className={styles['row']}
                                    data-testid="cybv3-cancel-button-container"
                                >
                                    <Button
                                        id={CybElementId.CYB_MFV2_PAY_BUTTON_ID}
                                        theme="no-border"
                                        size="medium"
                                        onClick={props.onClose}
                                    >
                                        Cancelar
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
    return props.isOpen ? ReactDOM.createPortal(component, container) : null;
};
